import { useDeletePendingPaymentMutation } from '@/services/accounts';
import { Box, Button, Icon, Modal, toast } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';

export interface PendingPaymentDetails {
  paymentId: string;
  amount: number;
  paymentDate: string;
  entryDate: string;
  paymentTypeDescription: string;
}

interface CancelPendingPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancelled: () => void;
  payment: PendingPaymentDetails;
}

export const CancelPendingPaymentModal = ({
  isOpen,
  onClose,
  onCancelled,
  payment,
}: CancelPendingPaymentModalProps) => {
  const { id: accountId = '' } = useParams<{ id: string }>();
  const [deletePendingPayment, { isLoading }] = useDeletePendingPaymentMutation();

  const handleCancel = useCallback(async () => {
    try {
      await deletePendingPayment({ accountId, paymentId: payment.paymentId }).unwrap();
      toast.success('Payment cancelled successfully');
      onCancelled();
    } catch (e: any) {
      const message = e?.data?.message ?? 'Error cancelling payment';
      toast.error(message);
    }
  }, [accountId, deletePendingPayment, onCancelled, payment.paymentId]);

  return (
    <Modal ariaLabelledBy="cancelPendingPaymentModal" isOpen={isOpen} onDismiss={onClose} maxWidth="480px">
      <Box
        id="cancelPendingPaymentModal"
        padding="lg"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        borderColor="separator"
        borderWidth="0 0 xs 0"
        style={{ flexShrink: 0 }}
      >
        <Box as="h4" fontSize={{ base: 'md', tablet: 'lg' }}>
          Cancel Pending Payment
        </Box>
        <button
          aria-label="close"
          type="button"
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 'var(--size-spacing-xs)',
            color: 'var(--color-text-body-primary)',
            lineHeight: 'var(--size-line-height-base)',
            borderRadius: 'var(--size-border-radius-sm)',
          }}
        >
          <Icon name="remove-light" />
        </button>
      </Box>
      <Modal.Body background="grey-50">
        <Box direction="column" childGap="md">
          <Box fontSize="sm" color="grey-700">
            Are you sure you want to cancel this scheduled payment? This action cannot be undone.
          </Box>
          <Box
            direction="column"
            childGap="sm"
            padding="md"
            background="white"
            borderColor="separator"
            borderWidth="xs"
            radius="md"
            fontSize="sm"
          >
            <Box direction="row" justifyContent="space-between">
              <Box color="grey-500">Amount</Box>
              <Box fontWeight="bold">${payment.amount.toFixed(2)}</Box>
            </Box>
            <Box direction="row" justifyContent="space-between">
              <Box color="grey-500">Scheduled date</Box>
              <Box>{DateTime.fromISO(payment.paymentDate).toLocaleString(DateTime.DATE_MED)}</Box>
            </Box>
            <Box direction="row" justifyContent="space-between">
              <Box color="grey-500">Payment type</Box>
              <Box>{payment.paymentTypeDescription}</Box>
            </Box>
            <Box direction="row" justifyContent="space-between">
              <Box color="grey-500">Entered on</Box>
              <Box>{DateTime.fromISO(payment.entryDate).toLocaleString(DateTime.DATE_MED)}</Box>
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button
          as="button"
          type="button"
          variant="secondary"
          tone="neutral"
          size="md"
          onClick={onClose}
          isDisabled={isLoading}
        >
          Go Back
        </Button>
        <Button
          as="button"
          type="button"
          variant="primary"
          tone="danger"
          size="md"
          onClick={handleCancel}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Cancel Payment
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
